import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ColumnPreferencesProvider } from './context/ColumnPreferencesContext';
import { DashboardPreferencesProvider } from './context/DashboardPreferencesContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { ShortcutProvider } from './keyboard/ShortcutContext';
import { CaptureProvider } from './capture/CaptureContext';
import { AssistantProvider } from './assistant/AssistantContext';
import Layout from './components/Layout';
import BillingLocked from './components/BillingLocked';
import Login from './pages/Login';
import Landing from './pages/Landing';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import VerifyEmail from './pages/VerifyEmail';
import Today from './pages/Today';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import AccountDetail from './pages/AccountDetail';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import RepFirms from './pages/RepFirms';
import RepFirmDetail from './pages/RepFirmDetail';
import Products from './pages/Products';
import Quotes from './pages/Quotes';
import QuoteDetail from './pages/QuoteDetail';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import ActionItems from './pages/ActionItems';
import Activities from './pages/Activities';
import Opportunities from './pages/Opportunities';
import OpportunityDetail from './pages/OpportunityDetail';
import Reports from './pages/Reports';
import ExecutiveReview from './pages/ExecutiveReview';
import Settings from './pages/Settings';
import Users from './pages/Users';
import UserPermissions from './pages/UserPermissions';

import ModuleManagement from './pages/ModuleManagement';
import QuoteTemplateEditor from './pages/QuoteTemplateEditor';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './pages/NotFound';
import { ToastProvider } from './context/ToastContext';
import ToastContainer from './components/ToastContainer';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function ModuleRoute({ moduleKey, children }) {
  const { isModuleVisible, user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  // If module is hidden, redirect to dashboard
  if (moduleKey && !isModuleVisible(moduleKey)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Pages that work signed out (or from an emailed link). They keep working
// while a workspace is locked for billing.
const PUBLIC_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password', '/accept-invite', '/verify-email'];

function AppRoutes() {
  const { user, loading, billingLocked } = useAuth();
  const { pathname } = useLocation();

  // Signed out, the front door is the public landing page; every other
  // signed-out path still goes to the sign-in page. Signed in, / is Today.
  if (!loading && !user && pathname === '/') return <Landing />;

  // A workspace locked for billing (trial over, or the subscription lapsed)
  // shows one screen in place of every signed-in page until it is paid for.
  if (!loading && user && billingLocked && !PUBLIC_PATHS.includes(pathname.replace(/\/+$/, ''))) {
    return <BillingLocked />;
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/" replace /> : <SignUp />} />
      {/* Pages an emailed link opens. They work signed out; /verify-email
          also works signed in. The token is in the URL fragment. */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Today />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/accounts" element={<ModuleRoute moduleKey="accounts"><Accounts /></ModuleRoute>} />
                <Route path="/accounts/:id" element={<ModuleRoute moduleKey="accounts"><AccountDetail /></ModuleRoute>} />
                <Route path="/contacts" element={<ModuleRoute moduleKey="contacts"><Contacts /></ModuleRoute>} />
                <Route path="/contacts/:id" element={<ModuleRoute moduleKey="contacts"><ContactDetail /></ModuleRoute>} />
                <Route path="/opportunities" element={<ModuleRoute moduleKey="opportunities"><Opportunities /></ModuleRoute>} />
                <Route path="/opportunities/new" element={<ModuleRoute moduleKey="opportunities"><OpportunityDetail /></ModuleRoute>} />
                <Route path="/opportunities/:id" element={<ModuleRoute moduleKey="opportunities"><OpportunityDetail /></ModuleRoute>} />
                <Route path="/rep-firms" element={<ModuleRoute moduleKey="rep_firms"><RepFirms /></ModuleRoute>} />
                <Route path="/rep-firms/:id" element={<ModuleRoute moduleKey="rep_firms"><RepFirmDetail /></ModuleRoute>} />
                <Route path="/products" element={<ModuleRoute moduleKey="products"><Products /></ModuleRoute>} />
                <Route path="/quotes" element={<ModuleRoute moduleKey="quotes"><Quotes /></ModuleRoute>} />
                <Route path="/quotes/:id" element={<ModuleRoute moduleKey="quotes"><QuoteDetail /></ModuleRoute>} />
                <Route path="/orders" element={<ModuleRoute moduleKey="orders"><Orders /></ModuleRoute>} />
                <Route path="/orders/:id" element={<ModuleRoute moduleKey="orders"><OrderDetail /></ModuleRoute>} />
                <Route path="/action-items" element={<ModuleRoute moduleKey="tasks"><ActionItems /></ModuleRoute>} />
                {/* Legacy path kept so existing bookmarks and links keep working */}
                <Route path="/tasks" element={<ModuleRoute moduleKey="tasks"><ActionItems /></ModuleRoute>} />
                <Route path="/activities" element={<ModuleRoute moduleKey="activities"><Activities /></ModuleRoute>} />
                <Route path="/reports" element={<ModuleRoute moduleKey="reports"><Reports /></ModuleRoute>} />
                <Route path="/executive-review" element={<ExecutiveReview />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id/permissions" element={<UserPermissions />} />

                <Route path="/quote-template-editor" element={<QuoteTemplateEditor />} />
                <Route path="/module-management" element={<ModuleManagement />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ColumnPreferencesProvider>
              <DashboardPreferencesProvider>
                <Router>
                  <BreadcrumbProvider>
                    <ToastProvider>
                      <ShortcutProvider>
                        <CaptureProvider>
                          <AssistantProvider>
                            <ToastContainer />
                            <AppRoutes />
                          </AssistantProvider>
                        </CaptureProvider>
                      </ShortcutProvider>
                    </ToastProvider>
                  </BreadcrumbProvider>
                </Router>
              </DashboardPreferencesProvider>
            </ColumnPreferencesProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
